/**
 * SSE → TanStack Query invalidation bridge for the campaign-detail page.
 * Mount once per campaign-detail page — handles all roster event types
 * and invalidates only the affected query keys.
 */
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ImportResult } from '@discovr/types';
import { useRosterSSE, sseManager } from '@discovr/common';
import { campaignKeys } from '../queryKeys';
import { shopifyKeys } from '../../shopify/queryKeys';
import { useCampaignCapabilitiesStore } from '../state/useCampaignCapabilitiesStore';
import type { ImportProgress } from '../state/useCampaignCapabilitiesStore';

// A report is heavy to build: a second event for the same report joins the
// request already running instead of cancelling and restarting it.
const KEEP_IN_FLIGHT = { cancelRefetch: false } as const;

// The coupon commit that follows a creator confirmation is asynchronous and
// publishes no event, so the Shopify reads are re-checked a few times after it.
const SHOPIFY_RECHECK_MS = [0, 3_000, 10_000, 25_000] as const;

export function useCampaignSSE(campaignId: string) {
  const queryClient = useQueryClient();
  const setImportResult = useCampaignCapabilitiesStore((s) => s.setImportResult);
  const setImportProgress = useCampaignCapabilitiesStore((s) => s.setImportProgress);
  // The import surface is admin-side only: shown to every admin-side user on the
  // campaign, never to the brand side. `lane` is the coarse app-side split
  // (admin app = 'admin', brand app = 'brand'); the BE already scopes the event
  // audience, so this is the FE half of "admin side only".
  const isAdminLane = useCampaignCapabilitiesStore((s) => s.lane === 'admin');
  // Report keys with a trailing refetch already queued behind their in-flight request.
  const trailingRefetches = React.useRef(new Set<string>());

  // The joined request was built before the event, so one more refetch follows it;
  // any number of events during that request share the one trailing refetch.
  const refreshReport = React.useCallback(
    (queryKey: readonly unknown[], trailing: boolean) => {
      const isFetching = queryClient.isFetching({ queryKey }) > 0;
      const joined = queryClient.invalidateQueries({ queryKey }, KEEP_IN_FLIGHT);
      const marker = JSON.stringify(queryKey);
      if (!trailing || !isFetching || trailingRefetches.current.has(marker)) return;
      trailingRefetches.current.add(marker);
      void joined.finally(() => {
        trailingRefetches.current.delete(marker);
        void queryClient.invalidateQueries({ queryKey }, KEEP_IN_FLIGHT);
      });
    },
    [queryClient],
  );

  const shopifyTimers = React.useRef(new Set<ReturnType<typeof setTimeout>>());
  React.useEffect(() => {
    const timers = shopifyTimers.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [campaignId]);
  // An event while a schedule is pending restarts the delayed checks only: a
  // burst costs one immediate refetch plus the schedule, not one per event.
  const refreshShopify = React.useCallback(
    (id: string) => {
      const timers = shopifyTimers.current;
      const scheduled = timers.size > 0;
      timers.forEach(clearTimeout);
      timers.clear();
      const run = () => {
        void queryClient.invalidateQueries({ queryKey: shopifyKeys.campaignConfig(id) }, KEEP_IN_FLIGHT);
        void queryClient.invalidateQueries({ queryKey: shopifyKeys.campaignCoupons(id) }, KEEP_IN_FLIGHT);
        void queryClient.invalidateQueries({ queryKey: shopifyKeys.campaignCreatorsAll(id) }, KEEP_IN_FLIGHT);
      };
      if (!scheduled) run();
      for (const delay of SHOPIFY_RECHECK_MS) {
        if (delay === 0) continue;
        const timer = setTimeout(() => {
          timers.delete(timer);
          run();
        }, delay);
        timers.add(timer);
      }
    },
    [queryClient],
  );

  // After any unintentional SSE drop + reconnect, invalidate the full roster so
  // events missed during the gap (creator_accepted, import.complete, etc.) are
  // reflected. The BE replays via Last-Event-ID but the replay ring buffer is
  // bounded — this is the safety net for longer gaps.
  React.useEffect(() => {
    sseManager.setReconnectHandler(() => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
      refreshReport(campaignKeys.reports(campaignId), true);
    });
    return () => {
      sseManager.setReconnectHandler(null);
    };
  }, [campaignId, queryClient, refreshReport]);

  useRosterSSE({
    enabled: !!campaignId,
    onEvent: (eventType, payload) => {
      if (payload.campaign_id && payload.campaign_id !== campaignId) return;

      const id = campaignId;
      const refreshGeneralReport = (trailing = true) => refreshReport(campaignKeys.generalReports(id), trailing);
      const refreshCreatorContentReport = (trailing = false) =>
        refreshReport(campaignKeys.creatorContentReports(id), trailing);

      switch (eventType) {
        // pending → accepted_by_brand: the row moves Pending → Approved within
        // the Creators tab. Not yet in production, so Scripts/Finalized are
        // untouched — only the Creators list + its pill counts change.
        case 'creator_accepted_by_brand':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
          refreshGeneralReport();
          break;

        // accepted_by_brand → accepted_by_admin: a transient admin sign-off that
        // auto-chains into creator_joined. Broad roster refresh so the approved
        // row leaves the Creators view as it advances into production.
        case 'creator_accepted_by_admin':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
          refreshGeneralReport();
          refreshShopify(id);
          break;

        // accepted_by_admin → accepted_by_creator: the creator joins production
        // and surfaces in the Scripts tab (script_pending). Refresh Finalized
        // membership too, since the creator is now part of the working roster.
        case 'creator_joined':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          refreshGeneralReport();
          refreshShopify(id);
          break;

        case 'creator_rejected':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
          // Rejecting a finalized creator changes Finalized-tab membership too —
          // mirror the sibling transition cases (creator_accepted, *_approved).
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          refreshGeneralReport();
          refreshShopify(id);
          break;

        // rejected_* / removed_by_admin → pending: admin reinstate. Only
        // Pending/Rejected are affected — reinstate never touches Finalized.
        case 'creator_reinstated':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
          refreshGeneralReport();
          break;

        case 'post_held':
        case 'post_hold_released':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          refreshGeneralReport();
          refreshCreatorContentReport(true);
          break;

        case 'post_approval_rolled_back':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          refreshGeneralReport();
          refreshCreatorContentReport(true);
          if (payload.entry_id) {
            queryClient.invalidateQueries({ queryKey: campaignKeys.scriptDetail(payload.entry_id) });
            queryClient.invalidateQueries({ queryKey: campaignKeys.contentDetail(payload.entry_id) });
            // The reopen note is posted without a new_message event.
            queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(payload.entry_id, 'script') });
            queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(payload.entry_id, 'content') });
          }
          break;

        // The entry leaves Confirmed or Approved (post deleted / selection cleared) and a
        // note lands in the selection thread without a new_message event.
        case 'creator_acceptance_rolled_back':
        case 'creator_brand_approval_rolled_back':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
          refreshGeneralReport();
          refreshCreatorContentReport(true);
          refreshShopify(id);
          if (payload.entry_id) {
            queryClient.invalidateQueries({ queryKey: campaignKeys.scriptDetail(payload.entry_id) });
            queryClient.invalidateQueries({ queryKey: campaignKeys.contentDetail(payload.entry_id) });
            queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(payload.entry_id, 'selection') });
            queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(payload.entry_id, 'script') });
            queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(payload.entry_id, 'content') });
          }
          break;

        case 'script_uploaded':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'scripts') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'finalized') });
          // Re-upload while a review drawer is open: the per-entry detail query
          // ([roster, entryId, script-detail, version]) is keyed separately from
          // the list, so refresh it too or the drawer shows the stale version.
          if (payload.entry_id) {
            queryClient.invalidateQueries({ queryKey: campaignKeys.scriptDetail(payload.entry_id) });
          }
          break;

        case 'script_approved':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'scripts') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'content') });
          // With the content stage off, an approved script lands the post in Live.
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'live-content') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'finalized') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          break;

        case 'content_uploaded':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'content') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'finalized') });
          // Refresh the open content review drawer's per-entry detail (keyed
          // separately from the list) so a re-upload isn't stale until reopened.
          if (payload.entry_id) {
            queryClient.invalidateQueries({ queryKey: campaignKeys.contentDetail(payload.entry_id) });
          }
          break;

        case 'content_approved':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'content') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'live-content') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'finalized') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisFinalized(id) });
          refreshGeneralReport();
          break;

        case 'campaign_creator_went_live':
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'live-content') });
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'finalized') });
          refreshGeneralReport();
          refreshCreatorContentReport(true);
          break;

        case 'campaign_kpis_updated':
          // Fired after roster transitions (accept/reject) and after each cron tick.
          refreshGeneralReport();
          break;

        case 'campaign_live_views_trend_updated':
          // The trend chart lives in the General Insights report, which KPI updates refresh too.
          refreshGeneralReport(false);
          break;

        case 'campaign_live_stats_updated':
          // Fired by the live-stats cron after per-creator stats are written.
          // Refreshes Live Content and Creator Content tabs so view counts
          // update without a manual reload.
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(id, 'live-content') });
          refreshCreatorContentReport(true);
          break;

        case 'import.complete': {
          // Admin-side only: the BE never fans import events out to the brand.
          if (!isAdminLane) break;
          const result = payload as ImportResult;
          // Refresh the roster so newly imported creators appear without a reload.
          queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
          setImportProgress(null); // clear bar before showing the result panel
          setImportResult(result);
          queryClient.setQueryData(campaignKeys.lastImport(id), result);
          // No success toast — the page-level ImportProgressPopup now shows the
          // result (and self-dismisses a clean one), so a toast would just be a
          // duplicate in the same corner.
          break;
        }

        case 'import.progress': {
          // Admin-side only; a new import supersedes any prior result still on
          // screen, so clear it before showing the fresh progress bar.
          if (!isAdminLane) break;
          const { done, total, imported, rejected } = payload as ImportProgress;
          if (typeof done === 'number' && typeof total === 'number') {
            setImportResult(null);
            setImportProgress({ done, total, imported: imported ?? 0, rejected: rejected ?? 0 });
          }
          break;
        }
      }
    },
  });
}
